// src/algorithms/initiativePlanner.ts
import { Person } from '../types';
import { mockPeople, mockProjects } from '../data/mockData';

export interface InitiativePlanningInput {
  initiativeName: string;
  description: string;
  timelineMonths: number;
  budget: number;
  requiredSkills: Array<{
    skill: string;
    level: number;
    headcount: number;
  }>;
}

export interface InitiativePlanningOutput {
  teamComposition: {
    recommendedTeam: Array<Person & { role: string; matchScore: number }>;
    totalCost: number;
    skillCoverage: number;
  };
  skillGapAnalysis: {
    gaps: Array<{
      skill: string;
      requiredLevel: number;
      bestAvailableLevel: number;
      gap: number;
      trainingRequired: boolean;
      trainingOptions: string[];
    }>;
    overallGapScore: number;
  };
  timelineAnalysis: {
    optimalTimeline: number; // months
    criticalPath: string[];
    risks: Array<{
      description: string;
      probability: 'high' | 'medium' | 'low';
      impact: 'high' | 'medium' | 'low';
      mitigation: string;
    }>;
  };
  recommendations: Array<{
    phase: 'preparation' | 'execution' | 'scaling';
    action: string;
    timeline: string;
    cost: number;
  }>;
  successMetrics: {
    probability: number;
    expectedROI: number;
    keySuccessFactors: string[];
  };
}

export function planInitiative(
  input: InitiativePlanningInput
): InitiativePlanningOutput {
  // Find optimal team composition
  const team = findOptimalTeam(input);
  
  // Analyze skill gaps
  const skillGaps = analyzeSkillGaps(team, input.requiredSkills);
  
  // Calculate timeline
  const timeline = calculateTimeline(team, input);
  
  // Generate recommendations
  const recommendations = generateRecommendations(team, skillGaps, timeline);
  
  // Calculate success probability
  const successProbability = calculateSuccessProbability(team, skillGaps, input);

  return {
    teamComposition: {
      recommendedTeam: team,
      totalCost: calculateTeamCost(team),
      skillCoverage: calculateSkillCoverage(team, input.requiredSkills),
    },
    skillGapAnalysis: {
      gaps: skillGaps,
      overallGapScore: calculateOverallGapScore(skillGaps),
    },
    timelineAnalysis: timeline,
    recommendations,
    successMetrics: {
      probability: successProbability,
      expectedROI: calculateExpectedROI(input.budget, successProbability),
      keySuccessFactors: identifySuccessFactors(team, input),
    },
  };
}

function findOptimalTeam(
  input: InitiativePlanningInput
): Array<Person & { role: string; matchScore: number }> {
  const availablePeople = mockPeople.filter(p => p.workload < 80);
  const team: Array<Person & { role: string; matchScore: number }> = [];
  
  // For each required skill set, find best match
  input.requiredSkills.forEach(skillReq => {
    for (let i = 0; i < skillReq.headcount; i++) {
      const bestMatch = findBestMatchForSkill(availablePeople, skillReq, team);
      if (bestMatch) {
        team.push({
          ...bestMatch.person,
          role: determineRole(bestMatch.person, skillReq),
          matchScore: bestMatch.score,
        });
        
        // Remove from available pool
        const index = availablePeople.findIndex(p => p.id === bestMatch.person.id);
        if (index > -1) {
          availablePeople.splice(index, 1);
        }
      }
    }
  });
  
  return team;
}

function findBestMatchForSkill(
  availablePeople: Person[],
  skillReq: InitiativePlanningInput['requiredSkills'][0],
  existingTeam: any[]
) {
  let bestMatch = null;
  let bestScore = 0;
  
  for (const person of availablePeople) {
    // Calculate match score
    const skillLevel = person.skillLevels[skillReq.skill] || 0;
    const baseScore = Math.min(100, (skillLevel / skillReq.level) * 100);
    
    // Adjust for workload (lower is better)
    const workloadPenalty = person.workload * 0.2;
    
    // Adjust for diversity of skills (prefer people who can cover multiple skills)
    const additionalSkills = Object.keys(person.skillLevels).length - 1;
    const diversityBonus = Math.min(20, additionalSkills * 5);
    
    // Adjust for career alignment
    const careerBonus = person.careerInterests.some(interest => 
      interest.toLowerCase().includes(skillReq.skill.toLowerCase())
    ) ? 15 : 0;
    
    const totalScore = baseScore - workloadPenalty + diversityBonus + careerBonus;
    
    if (totalScore > bestScore) {
      bestScore = totalScore;
      bestMatch = { person, score: Math.round(totalScore) };
    }
  }
  
  return bestMatch;
}

function determineRole(person: Person, skillReq: any): string {
  const skillLevel = person.skillLevels[skillReq.skill] || 0;
  
  if (skillLevel >= 9) return `${skillReq.skill} Lead`;
  if (skillLevel >= 7) return `Senior ${skillReq.skill} Engineer`;
  if (skillLevel >= 5) return `${skillReq.skill} Engineer`;
  return `Junior ${skillReq.skill} Developer`;
}

function analyzeSkillGaps(
  team: any[],
  requiredSkills: InitiativePlanningInput['requiredSkills']
) {
  const gaps: InitiativePlanningOutput['skillGapAnalysis']['gaps'] = [];
  
  requiredSkills.forEach(skillReq => {
    const teamMembersWithSkill = team.filter(member => 
      member.skillLevels[skillReq.skill] !== undefined
    );
    
    if (teamMembersWithSkill.length === 0) {
      gaps.push({
        skill: skillReq.skill,
        requiredLevel: skillReq.level,
        bestAvailableLevel: 0,
        gap: skillReq.level,
        trainingRequired: true,
        trainingOptions: getTrainingOptions(skillReq.skill),
      });
    } else {
      const bestLevel = Math.max(...teamMembersWithSkill.map(m => m.skillLevels[skillReq.skill]));
      const gap = Math.max(0, skillReq.level - bestLevel);
      
      gaps.push({
        skill: skillReq.skill,
        requiredLevel: skillReq.level,
        bestAvailableLevel: bestLevel,
        gap,
        trainingRequired: gap >= 2,
        trainingOptions: gap >= 2 ? getTrainingOptions(skillReq.skill) : [],
      });
    }
  });
  
  return gaps;
}

function getTrainingOptions(skill: string): string[] {
  const trainingMap: Record<string, string[]> = {
    'Java': ['Advanced Java Patterns (40h)', 'Spring Boot Certification (60h)'],
    'Python': ['Python for Data Science (50h)', 'Advanced Python (45h)'],
    'AWS': ['AWS Solutions Architect (60h)', 'AWS DevOps (55h)'],
    'ML': ['Machine Learning Specialization (80h)', 'Deep Learning (70h)'],
    'Security': ['Security+ Certification (40h)', 'Cybersecurity Fundamentals (50h)'],
  };
  
  return trainingMap[skill] || [`${skill} Fundamentals Course (40h)`];
}

function calculateTimeline(
  team: any[],
  input: InitiativePlanningInput
): InitiativePlanningOutput['timelineAnalysis'] {
  const baseTimeline = input.timelineMonths;
  
  // Adjust based on team experience
  const avgTenure = team.reduce((sum, member) => sum + member.tenureMonths, 0) / team.length;
  const experienceFactor = avgTenure > 24 ? 0.9 : avgTenure > 12 ? 1.0 : 1.2;
  
  // Adjust based on skill gaps
  const skillGaps = analyzeSkillGaps(team, input.requiredSkills);
  const gapFactor = skillGaps.reduce((sum, gap) => sum + gap.gap, 0) / skillGaps.length;
  const gapMultiplier = 1 + (gapFactor * 0.1);
  
  const optimalTimeline = Math.round(baseTimeline * experienceFactor * gapMultiplier * 10) / 10;
  
  return {
    optimalTimeline,
    criticalPath: identifyCriticalPath(team, input),
    risks: [
      {
        description: 'Key team member attrition',
        probability: 'medium',
        impact: 'high',
        mitigation: 'Cross-training and documentation',
      },
      {
        description: 'Scope creep',
        probability: 'high',
        impact: 'medium',
        mitigation: 'Strict change control process',
      },
      {
        description: 'Technology adoption delays',
        probability: 'low',
        impact: 'high',
        mitigation: 'Early proof-of-concepts',
      },
    ],
  };
}

function identifyCriticalPath(team: any[], input: InitiativePlanningInput): string[] {
  const criticalPath: string[] = [];
  
  // People on critical path
  const criticalMembers = team.filter(member => member.isCriticalPath);
  if (criticalMembers.length > 0) {
    criticalPath.push(`Dependency on ${criticalMembers.length} critical team members`);
  }
  
  // Skills that are hardest to find
  const rareSkills = input.requiredSkills.filter(req => 
    mockPeople.filter(p => (p.skillLevels[req.skill] || 0) >= req.level).length < 3
  );
  
  if (rareSkills.length > 0) {
    criticalPath.push(`Limited availability for ${rareSkills.map(s => s.skill).join(', ')} skills`);
  }
  
  // Budget constraints
  if (input.budget < 500000) {
    criticalPath.push('Tight budget constraints');
  }
  
  return criticalPath;
}

function generateRecommendations(
  team: any[],
  skillGaps: any[],
  timeline: any
): InitiativePlanningOutput['recommendations'] {
  const recommendations: InitiativePlanningOutput['recommendations'] = [];
  
  // Preparation phase
  const significantGaps = skillGaps.filter(gap => gap.gap >= 2);
  if (significantGaps.length > 0) {
    recommendations.push({
      phase: 'preparation',
      action: `Pre-initiative training for ${significantGaps.length} key skills`,
      timeline: '1-2 months before start',
      cost: calculateTrainingCost(significantGaps),
    });
  }
  
  // Execution phase
  if (team.length < 5) {
    recommendations.push({
      phase: 'execution',
      action: 'Hire 1-2 contractors for peak periods',
      timeline: 'Months 2-3 of initiative',
      cost: 80000,
    });
  }
  
  // Scaling phase
  recommendations.push({
    phase: 'scaling',
    action: 'Knowledge transfer sessions to wider team',
    timeline: 'Last month of initiative',
    cost: 15000,
  });
  
  return recommendations;
}

function calculateTrainingCost(gaps: any[]): number {
  return gaps.length * 5000; // $5k per training
}

function calculateTeamCost(team: any[]): number {
  return team.reduce((total, member) => total + member.costPerMonth, 0) * 12;
}

function calculateSkillCoverage(
  team: any[],
  requiredSkills: InitiativePlanningInput['requiredSkills']
): number {
  let totalCoverage = 0;
  
  requiredSkills.forEach(skillReq => {
    const teamMembersWithSkill = team.filter(member => 
      member.skillLevels[skillReq.skill] !== undefined
    );
    
    if (teamMembersWithSkill.length >= skillReq.headcount) {
      totalCoverage += 100;
    } else if (teamMembersWithSkill.length > 0) {
      totalCoverage += (teamMembersWithSkill.length / skillReq.headcount) * 100;
    }
  });
  
  return Math.round(totalCoverage / requiredSkills.length);
}

function calculateOverallGapScore(gaps: any[]): number {
  if (gaps.length === 0) return 0;
  
  const totalGap = gaps.reduce((sum, gap) => sum + gap.gap, 0);
  const maxPossibleGap = gaps.length * 10; // Assuming level 10 is max
  return Math.round((totalGap / maxPossibleGap) * 100);
}

function calculateSuccessProbability(
  team: any[],
  skillGaps: any[],
  input: InitiativePlanningInput
): number {
  let probability = 70; // Base probability
  
  // Team factors
  const avgProductivity = team.reduce((sum, member) => sum + member.productivityScore, 0) / team.length;
  probability += (avgProductivity - 75) / 5;
  
  // Skill gap factors
  const overallGapScore = calculateOverallGapScore(skillGaps);
  probability -= overallGapScore / 2;
  
  // Budget factors
  const budgetAdequacy = input.budget / (team.length * 15000 * 12);
  probability += budgetAdequacy > 1.2 ? 10 : budgetAdequacy > 0.8 ? 0 : -10;
  
  // Timeline factors
  const timelineRealism = input.timelineMonths >= 6 ? 5 : 0;
  probability += timelineRealism;
  
  return Math.max(0, Math.min(100, Math.round(probability)));
}

function calculateExpectedROI(budget: number, probability: number): number {
  const baseROI = budget * 3; // Assume 3x return for successful projects
  const expectedValue = (probability / 100) * baseROI;
  return Math.round(expectedValue);
}

function identifySuccessFactors(team: any[], input: InitiativePlanningInput): string[] {
  const factors: string[] = [];
  
  if (team.length >= 5) {
    factors.push('Adequate team size for initiative scope');
  }
  
  const experiencedMembers = team.filter(member => member.tenureMonths > 24);
  if (experiencedMembers.length >= 2) {
    factors.push('Experienced leadership within team');
  }
  
  if (input.budget > 500000) {
    factors.push('Sufficient budget allocation');
  }
  
  if (input.timelineMonths >= 6) {
    factors.push('Realistic timeline');
  }
  
  return factors;
}
